/**
 * ArchiveTimeline — Feb 2026 · Iter94
 *
 * Scrollable timeline showing every archive/restore cycle for a
 * project. Used inside the Project Details audit banner to give
 * reviewers full lifecycle history at a glance.
 */
import { useQuery } from "@tanstack/react-query";
import { Clock, ArrowUUpLeft, Archive as ArchiveIcon } from "@phosphor-icons/react";
import { getProjectArchiveEvents } from "@/lib/api";

const REASON_STYLE = {
  won:       { bg: "bg-emerald-100 text-emerald-800", label: "🏆 Won" },
  lost:      { bg: "bg-rose-100 text-rose-800",       label: "😔 Lost" },
  paused:    { bg: "bg-amber-100 text-amber-800",     label: "⏸ Paused" },
  completed: { bg: "bg-slate-100 text-slate-700",     label: "✅ Done" },
  other:     { bg: "bg-slate-100 text-slate-700",     label: "❓ Other" },
};

export default function ArchiveTimeline({ projectId }) {
  const q = useQuery({
    queryKey: ["archive-events", projectId],
    queryFn:  () => getProjectArchiveEvents(projectId),
    enabled:  !!projectId,
    staleTime: 30_000,
  });
  const events = q.data?.events || [];
  if (q.isLoading) return (
    <div className="text-[11px] text-slate-500 italic" data-testid="archive-timeline-loading">
      Loading timeline…
    </div>
  );
  if (!events.length) return null;
  return (
    <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-amber-200 bg-white/60
                     divide-y divide-amber-100" data-testid="archive-timeline">
      {events.map((e, i) => {
        const isArchive = e.event === "archived";
        const style = REASON_STYLE[e.reason] || {};
        return (
          <div
            key={e.id}
            className="px-3 py-2 flex items-start gap-3 text-[11.5px]"
            data-testid={`archive-timeline-row-${i}`}
            data-event={e.event}
          >
            <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center
                                ${isArchive ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
              {isArchive ? <ArchiveIcon size={12} /> : <ArrowUUpLeft size={12} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-800 uppercase text-[10.5px] tracking-widest">
                  {isArchive ? "Archived" : "Restored"}
                </span>
                {isArchive && e.reason && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[9.5px] font-mono uppercase tracking-widest ${style.bg}`}
                              data-testid={`archive-timeline-reason-${i}`}>
                    {style.label || e.reason}
                  </span>
                )}
                <span className="text-slate-500 font-mono inline-flex items-center gap-1">
                  <Clock size={10} /> {new Date(e.at).toLocaleString()}
                </span>
              </div>
              <div className="text-slate-600 mt-0.5 text-[11px]">
                {e.actor ? <span>by {e.actor}</span> : <span className="italic">actor unknown</span>}
              </div>
              {e.note && (
                <div className="mt-1 text-[10.5px] text-slate-600 italic border-l-2 border-amber-200 pl-2"
                          data-testid={`archive-timeline-note-${i}`}>
                  “{e.note}”
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
